import type { CreditAmount, UserAccountDoc } from '@flintmint/core-shared/firestore/schemas';
import {
  type Tier,
  computeTier,
  creditsLeftPercent,
  effectiveMonthlyQuota,
  isCreditAmount,
  wholeCreditsLeft,
} from '@flintmint/core-shared/utils';

import { canBuyRecharge, canOpenPortal, heldChargeUrl, isPastDue, isTrialEligible } from '@/features/billing/billing-state';

export type EnergyAction =
  | 'exploreTrial'
  | 'comparePlans'
  | 'startPlan'
  | 'payHeldCharge'
  | 'undoCancel'
  | 'updatePayment'
  | 'recharge';
export type BatteryLevel = 0 | 1 | 2 | 3 | 4;
export type EnergyTone = 'danger' | 'warning' | 'calm';
export type RechargeStatus = 'Ready' | 'In use';
export type EnergyAmount = { percentLeft: number; joulesLeft: number; joulesTotal: number };
export type EnergyView = EnergyAmount & {
  label: 'AI Preview' | 'Trial Energy' | 'Monthly Energy';
  suffix?: 'One-time' | 'No reset' | 'Trial active';
  action?: EnergyAction;
  /** The Joule balance, once the account has been credited any. */
  recharge?: EnergyAmount;
};

/** The Energy the account is spending: Introductory on Free, Trial or Monthly on a paid plan; `undefined` for a comped or missing account. */
export const getEnergyView = (account: UserAccountDoc | undefined): EnergyView | undefined => {
  if (!account) return undefined;
  const tier = computeTier(account);
  if (tier === 'comped') return undefined;

  const view = quotaEnergyView(account, tier);
  const balance = creditedJouleBalance(account);
  return balance ? { ...view, recharge: amountOf(balance) } : view;
};

/** A Trial whose Energy is used up and that has no scheduled end: the plan can be started early, or its held charge paid. */
export const isTrialEnergyUsedUp = (account: UserAccountDoc | undefined) => {
  const view = getEnergyView(account);
  return (view?.action === 'startPlan' || view?.action === 'payHeldCharge') && view.percentLeft === 0;
};

/** The scheduled end of a cancelled Trial whose Energy is used up, where undoing the cancellation restores its plan. */
export const cancelledTrialEnd = (account: UserAccountDoc | undefined) =>
  getEnergyView(account)?.action === 'undoCancel' ? account?.stripeSubscription?.cancelAt : undefined;

/**
 * A renewal in its grace period whose grace allowance is used up: AI was refused in the current period (the account
 * does not spend its Joule balance, so the grace allowance is the only stop), or no Monthly Energy is left, which
 * the grace allowance never exceeds.
 */
export const isGraceAllowanceUsedUp = (account: UserAccountDoc | undefined, refusedPeriodStart: string | undefined) =>
  isRenewalInGrace(account) &&
  (refusedPeriodStart === account?.stripeSubscription?.currentPeriodStart || getEnergyView(account)?.percentLeft === 0);

/** A paid subscription whose renewal failed and that Stripe is retrying, while AI runs on the grace allowance. */
export const isRenewalInGrace = (account: UserAccountDoc | undefined) =>
  computeTier(account) === 'paid' && isPastDue(account?.stripeSubscription);

/** An active subscriber out of Monthly Energy with no Joules left, bought or never bought: AI waits on a Recharge. */
export const isPausedForRecharge = (account: UserAccountDoc | undefined) => {
  const view = getEnergyView(account);
  return view?.action === 'recharge' && view.percentLeft === 0;
};

/** The Joule balance once the account has been credited any; every accepted account holds 0 / 0 until then. */
export const creditedJouleBalance = (account: UserAccountDoc | undefined) => {
  const balance = account?.creditBalance;
  return isCreditAmount(balance) && balance.total > 0 ? balance : undefined;
};

/** The amount the chip shows: the Joule balance once the quota is empty and the account spends the balance, else the quota. */
export const chipAmount = (account: UserAccountDoc, view: EnergyView): EnergyAmount =>
  view.percentLeft === 0 && view.recharge && spendsJouleBalance(account) ? view.recharge : view;

/**
 * The Joule balance's status: In use while the account spends it, Ready while it would once the quota runs out;
 * none once it is used up or while the account does not spend it.
 */
export const rechargeStatus = (account: UserAccountDoc, view: EnergyView): RechargeStatus | undefined => {
  if (!view.recharge || view.recharge.joulesLeft === 0 || !spendsJouleBalance(account)) return undefined;
  return chipAmount(account, view) === view.recharge ? 'In use' : 'Ready';
};

/**
 * The color of the quota's bar, and of its icon while any is left. Red once nothing the account spends is left. While the
 * account spends its Joule balance (`rechargeTone` colors that row) or is `past_due`, no warning. On Free, orange from
 * `quotaPercentUsedWarning` percent of the AI Preview quota used, none while the app settings load. Otherwise red at a
 * quarter left or less, orange at half, and on Monthly Energy also orange from `quotaPercentUsedWarning` percent used.
 */
export const energyTone = (
  account: UserAccountDoc,
  view: EnergyView,
  quotaPercentUsedWarning: number | undefined
): EnergyTone => {
  const amount = chipAmount(account, view);
  if (amount.percentLeft === 0) return 'danger';
  if (amount !== view || isPastDue(account.stripeSubscription)) return 'calm';
  if (computeTier(account) === 'free') {
    return isWarningPassed(account.aiPreviewQuota, quotaPercentUsedWarning) ? 'warning' : 'calm';
  }
  const monthlyWarningPassed =
    account.stripeSubscription?.status !== 'trialing' &&
    isWarningPassed(effectiveMonthlyQuota(account, new Date()), quotaPercentUsedWarning);
  return levelTone(view.percentLeft, monthlyWarningPassed);
};

/**
 * The color of the Joule balance's bar and icon while the account spends it (Ready or In use), as on Monthly Energy:
 * red at a quarter left or less, orange at half or from `quotaPercentUsedWarning` percent used. Calm otherwise, which
 * the row draws grey while Ready and green while In use.
 */
export const rechargeTone = (
  account: UserAccountDoc,
  view: EnergyView,
  quotaPercentUsedWarning: number | undefined
): EnergyTone => {
  if (!view.recharge || !rechargeStatus(account, view)) return 'calm';
  return levelTone(view.recharge.percentLeft, isWarningPassed(account.creditBalance, quotaPercentUsedWarning));
};

/**
 * The chip's color: `energyTone`, or `rechargeTone` while Recharge is In use, except that orange on Monthly Energy or
 * Recharge stays on its row and the chip stays grey, or green while Recharge is In use.
 */
export const chipTone = (account: UserAccountDoc, view: EnergyView, quotaPercentUsedWarning: number | undefined): EnergyTone => {
  const inUse = rechargeStatus(account, view) === 'In use';
  const tone = inUse ? rechargeTone(account, view, quotaPercentUsedWarning) : energyTone(account, view, quotaPercentUsedWarning);
  return tone === 'warning' && (inUse || view.label === 'Monthly Energy') ? 'calm' : tone;
};

/** Bars of the battery icon: one per quarter of the Energy left, so any Energy left shows at least one. */
export const batteryLevel = (percentLeft: number): BatteryLevel => Math.ceil(percentLeft / 25) as BatteryLevel;

const quotaEnergyView = (account: UserAccountDoc, tier: Tier): EnergyView => {
  const subscription = account.stripeSubscription;

  if (tier === 'free') {
    const view = quotaView('AI Preview', account.aiPreviewQuota);
    return {
      ...view,
      suffix: view.percentLeft > 0 ? 'One-time' : 'No reset',
      action: canOpenPortal(account) ? 'updatePayment' : isTrialEligible(account) ? 'exploreTrial' : 'comparePlans',
    };
  }

  if (subscription?.status === 'trialing') {
    const view = quotaView('Trial Energy', account.monthlyQuota);
    const usedUp = view.percentLeft === 0;
    if (subscription.cancelAt !== undefined) return usedUp && account.stripeCustomerId ? { ...view, action: 'undoCancel' } : view;
    // A held early start is paid at any Trial Energy level; the plan is offered here only once the Energy is used up.
    if (heldChargeUrl(account)) {
      return usedUp ? { ...view, suffix: 'Trial active', action: 'payHeldCharge' } : { ...view, action: 'payHeldCharge' };
    }
    if (usedUp) return { ...view, suffix: 'Trial active', action: 'startPlan' };
    return view;
  }

  const view = quotaView('Monthly Energy', effectiveMonthlyQuota(account, new Date()));
  if (isPastDue(subscription) && canOpenPortal(account)) return { ...view, action: 'updatePayment' };
  if (canBuyRecharge(account) && wholeCreditsLeft(account.creditBalance) === 0) return { ...view, action: 'recharge' };
  return view;
};

const quotaView = (label: EnergyView['label'], quota: CreditAmount | undefined): EnergyView => ({ label, ...amountOf(quota) });

const amountOf = (amount: CreditAmount | undefined): EnergyAmount => ({
  percentLeft: creditsLeftPercent(amount),
  joulesLeft: wholeCreditsLeft(amount),
  joulesTotal: isCreditAmount(amount) ? amount.total : 0,
});

/** The spend order: only a paid account that is not `past_due` spends its Joule balance, after its monthly quota. */
const spendsJouleBalance = (account: UserAccountDoc) => computeTier(account) === 'paid' && !isPastDue(account.stripeSubscription);

/** Red at a quarter left or less, orange at half or once the threshold warning has passed. */
const levelTone = (percentLeft: number, warningPassed: boolean): EnergyTone => {
  const level = batteryLevel(percentLeft);
  if (level <= 1) return 'danger';
  return level === 2 || warningPassed ? 'warning' : 'calm';
};

/** At least `quotaPercentUsedWarning` percent of the amount used; never while the app settings load. */
const isWarningPassed = (amount: CreditAmount | undefined, quotaPercentUsedWarning: number | undefined) =>
  quotaPercentUsedWarning !== undefined && percentUsed(amount) >= quotaPercentUsedWarning;

/** `used` ÷ `total` × 100; only called while the amount has Energy left, so `total` is above 0. */
const percentUsed = (quota: CreditAmount | undefined) => (isCreditAmount(quota) ? (quota.used / quota.total) * 100 : 100);
